import type { PatientResponse, AlertResponse, PatientStatus, AlertSeverity } from "../../../shared/types/patient";
import {
  alertSeverityLabel,
  alertTypeLabel,
  formatDate,
  formatPhone,
  genderLabel,
  statusLabel,
} from "../../../shared/format";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { PatientAvatar } from "./PatientAvatar";

interface Props {
  patient: PatientResponse;
  alerts: AlertResponse[];
  actions?: React.ReactNode;
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_VARIANT: Record<PatientStatus, BadgeVariant> = {
  ACTIVE: "success",
  ARCHIVED: "muted",
  MERGED: "muted",
  DECEASED: "muted",
};

const ALERT_VARIANT: Record<AlertSeverity, BadgeVariant> = {
  INFO: "secondary",
  WARNING: "warning",
  CRITICAL: "destructive",
};

export function PatientHeader({ patient, alerts, actions }: Props) {
  return (
    <header className="flex flex-col gap-4 rounded-md border border-border bg-card p-4 sm:flex-row sm:items-start">
      <PatientAvatar
        fullName={patient.fullName}
        zaloAvatarUrl={patient.zaloAvatarUrl}
        avatarUrl={patient.avatarUrl}
        className="size-14 text-lg"
      />
      <div className="min-w-0 flex-1 space-y-2">
        <h1 className="text-xl font-semibold text-foreground">
          {patient.fullName}{" "}
          {patient.patientCode && (
            <span className="font-mono text-sm font-normal text-muted-foreground">
              · {patient.patientCode}
            </span>
          )}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>
            {patient.gender ? genderLabel[patient.gender] : "—"}
            {patient.age != null && ` · ${patient.age} tuổi`}
          </span>
          <span>Sinh: {formatDate(patient.dateOfBirth)}</span>
          <span>SĐT: {formatPhone(patient.phoneE164)}</span>
          {patient.email && <span>Email: {patient.email}</span>}
          <span className="flex items-center gap-1">
            Trạng thái:{" "}
            <Badge variant={STATUS_VARIANT[patient.status]}>{statusLabel[patient.status]}</Badge>
          </span>
        </div>
        {alerts.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {alerts.map((a) => (
              <Badge
                key={a.id}
                variant={ALERT_VARIANT[a.severity]}
                title={a.descriptionVi ?? ""}
              >
                {alertTypeLabel[a.alertType]} · {alertSeverityLabel[a.severity]} · {a.titleVi}
              </Badge>
            ))}
          </div>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}
